import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Row, Col } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserData } from "features/user/userSlice";
import { Divider, Typography } from "antd";
const { Title, Paragraph } = Typography;

const Login: React.FC = () => {
  const navigate = useNavigate();
  let dispatch = useDispatch();
  // let userData = useSelector(selectUserData);

  const [form] = Form.useForm();
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  // Countdown effect for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Function to send the verification code
  const sendVerification = async () => {
    try {
      // Get the email value from form
      const email = form.getFieldValue("email");
      if (!email) {
        message.error("Please enter your email address");
        return;
      }
      setIsSending(true);
      // Replace with your actual API endpoint for sending the verification code
      const response = await axios.post(
        "/api/accounts/send_verification_code/",
        { email }
      );
      if (response.status === 200) {
        message.success("Verification code sent");
        // Start a 60-second countdown to disable sending again immediately
        setCountdown(60 * 5);
      } else {
        message.error("Failed to send verification email");
      }
    } catch (error: any) {
      // DRF responses can be: {detail: "..."} (e.g. 429 throttle), {message: "..."},
      // {email: ["..."]} (validation), or arbitrary text. Surface whichever we get.
      const data = error?.response?.data;
      let msg = "Failed to send verification email";
      if (typeof data === "string" && data) {
        msg = data;
      } else if (data && typeof data === "object") {
        msg =
          data.message ||
          data.detail ||
          Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join("; ") ||
          msg;
      } else if (error?.message) {
        msg = `${msg}: ${error.message}`;
      }
      message.error(msg);
    } finally {
      setIsSending(false);
    }
  };

  // Form submission handler for login
  const onFinish = async (values: any) => {
    try {
      // Replace with your actual API endpoint for login
      const response = await axios.post(
        "/api/accounts/verify_code_and_login/",
        {
          email: values.email,
          verification_code: values.verificationCode,
          password: values.password,
        }
      );

      if (response.status === 200) {
        message.success("Login successful");
        dispatch(setUserData(response.data));
        navigate("/home/", { replace: true });
      }
    } catch (error: any) {
      if (error.response) {
        const response = error.response;
        if (response.status === 403) {
          // 403 typically means inactive account / pending activation.
          setErrorMessage(
            response.data?.detail || "Account is not active. Contact your admin."
          );
        } else {
          // Build the most useful message we can from a DRF error body:
          //   { detail: "..." }                  → throttle / generic
          //   { message: "..." }                 → some custom views
          //   { field: ["msg"], field2: [...] }  → serializer validation
          //   { non_field_errors: ["..."] }      → cross-field validation
          //   "raw string"                       → plain text
          const data = response.data;
          let msg = "Login failed, please try again";
          if (typeof data === "string" && data) {
            msg = data;
          } else if (data && typeof data === "object") {
            msg =
              data.detail ||
              data.message ||
              (Array.isArray(data.non_field_errors)
                ? data.non_field_errors.join(" ")
                : "") ||
              Object.entries(data)
                .filter(([k]) => k !== "non_field_errors")
                .map(
                  ([k, v]) =>
                    `${k}: ${Array.isArray(v) ? v.join(", ") : v}`
                )
                .join("; ") ||
              msg;
          }
          message.error(msg);
        }
      } else {
        message.error(
          error?.message
            ? `Login failed: ${error.message}`
            : "Login failed, please try again"
        );
      }
    }
  };

  if (errorMessage) {
    return (
      <Row justify="center" style={{ marginTop: 100 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ padding: 24, background: "#fff", borderRadius: 4 }}>
            <div className="flex flex-col items-center">
              <Typography>
                <Title level={2}>Login failed</Title>
                <Paragraph>{errorMessage}</Paragraph>
              </Typography>
              <Divider />
              <div>
                <Button
                  type="link"
                  onClick={() => navigate("/send_activation_mail")}
                >
                  Go to Send Activation Mail
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row justify="center" style={{ marginTop: 100 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ padding: 24, background: "#fff", borderRadius: 4 }}>
            <h2 style={{ textAlign: "center" }}>Login</h2>
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              //labelCol={{ flex: "100px" }} // 固定标签宽度
              //wrapperCol={{ flex: "auto" }} // 剩余空间给输入框
              //labelAlign="left"
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email address",
                  },
                  { type: "email", message: "Please input a valid email" },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="example@example.com"
                />
              </Form.Item>

              <Form.Item
                label="Verification Code"
                name="verificationCode"
                rules={[
                  {
                    required: true,
                    message: "Please enter the verification code",
                  },
                ]}
              >
                <Row gutter={8} align="middle">
                  <Col span={16}>
                    <Input placeholder="Enter verification code" />
                  </Col>
                  <Col span={8}>
                    <Button
                      onClick={sendVerification}
                      disabled={isSending || countdown > 0}
                      style={{ width: "100%" }}
                    >
                      {countdown > 0 ? `${countdown}s` : "Send Code"}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters",
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter Password"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: "100%" }}
                >
                  Login
                </Button>
              </Form.Item>
            </Form>
            <div
              style={{ marginTop: 16 }}
              className="flex justify-between items-center"
            >
              {/* <Link to="/register">Register</Link>
            <span style={{ float: "right" }}>
              <Link to="/forgot_password">Forgot Password</Link>
            </span> */}
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => navigate("/register")}
              >
                Register
              </Button>
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => navigate("/forgot_password")}
              >
                Forgot Password
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Login;
